/**
 * KeralaTravelsHome — travel-focused sibling preview.
 *
 * Preview modes:
 *   /travels                → always shows this page
 *   /?site=keralatravels    → swaps the homepage for this page
 *   hotel-booking-beta.emergent.host → resolved via HOST_ALIASES in
 *                                       siteConfig.js
 *
 * Content strategy — everything travel-adjacent:
 *   • Hero with Kerala Travels branding + houseboat/hotel CTA
 *   • Every Kerala district (14 stamps → district pages)
 *   • Featured tourism cities (Kochi / Munnar / Alleppey / Kovalam …)
 *     each showing their attractions + parks + museums via the
 *     Geographic Discovery Engine's POI directory
 *   • Fresh `site_source=keralatravels` pulses when publishers ship
 *     posts back via the shared CMS API key
 *
 * Everything comes from the parent Social Pulse API — nothing here
 * lives outside the shared backend. Palette is banana-leaf green +
 * brass gold + houseboat teak (kept local, doesn't leak into the
 * parent design tokens).
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Loader2, Sparkles, MapPin, Waves, Hotel, Palmtree, Camera, ArrowRight,
} from "lucide-react";
import PlaceStamp from "@/components/PlaceStamp";
import PlaceKindTag from "@/components/PlaceKindTag";
import UnsplashPhoto, { UnsplashCredit } from "@/components/UnsplashPhoto";
import LocationWeatherPanel from "@/components/LocationWeatherPanel";

const API = process.env.REACT_APP_BACKEND_URL;

// Palette drawn from a Kerala houseboat + teak deck + brass lamp
// reference. Kept in-component so tweaks never touch the parent's
// global design system.
const K = {
  bg:        "#FBF7EA",
  ink:       "#0D3B2E",
  inkSoft:   "#3C6B5D",
  accent:    "#0E7C55",          // banana-leaf green
  accentSoft:"#B7E4C7",
  gold:      "#C08A2E",          // brass lamp
  goldSoft:  "#F5E6C4",
  teak:      "#7A4A2B",          // deck wood
  teakSoft:  "#E7D3BE",
};

const HERO_LINES = [
  "Hotels, houseboats and hidden districts — plan your Kerala trip on the same map locals live on.",
  "Attractions, parks, museums and monuments from every corner of God's Own Country.",
];

// Featured tourism cities — the ones travellers actually search for.
// Each is fetched via the parent's POI directory (`attractions`,
// `parks`, `museums`) so this page has zero hand-curated content.
const FEATURED_CITIES = [
  { slug: "india/kerala/kochi",              name: "Kochi",              tagline: "Port city, spice-route history" },
  { slug: "india/kerala/thiruvananthapuram", name: "Thiruvananthapuram", tagline: "Capital · palace and coast" },
  { slug: "india/kerala/kozhikode",          name: "Kozhikode",          tagline: "Calicut beach and bakery street" },
  { slug: "india/kerala/thrissur",           name: "Thrissur",           tagline: "Cultural capital · Pooram" },
];

const TRAVEL_CATEGORIES = [
  { slug: "attractions", label: "Attractions", Icon: Camera },
  { slug: "parks",       label: "Parks",       Icon: Palmtree },
  { slug: "museums",     label: "Museums",     Icon: Sparkles },
  { slug: "monuments",   label: "Monuments",   Icon: MapPin },
];

export default function KeralaTravelsHome() {
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pulses,  setPulses]  = useState([]);
  const [heroPhoto, setHeroPhoto] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/locations-children/india/kerala`, { params: { limit: 100 } })
      .then((r) => alive && setPlaces(r.data?.items || []))
      .catch(() => alive && setPlaces([]))
      .finally(() => alive && setLoading(false));
    // Latest keralatravels-tagged pulses via the multi-site CMS.
    axios.get(`${API}/api/pulse-items`, { params: { site_source: "keralatravels", limit: 6 } })
      .then((r) => alive && setPulses(r.data?.items || []))
      .catch(() => { /* non-fatal */ });
    // Hero background photo — server proxy caches for 1h so we don't
    // hammer Unsplash. Failure is silent — the gradient still looks great.
    axios.get(`${API}/api/unsplash/search`, {
      params: { q: "kerala houseboat backwaters", per_page: 1, orientation: "landscape" },
    })
      .then((r) => alive && setHeroPhoto(r.data?.results?.[0] || null))
      .catch(() => { /* non-fatal */ });
    return () => { alive = false; };
  }, []);

  const districts = useMemo(
    () => places.filter((p) => (p.kind || "").toLowerCase() === "district")
                .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [places]
  );

  return (
    <div style={{ background: K.bg, color: K.ink }} data-testid="keralatravels-home">
      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${K.accent} 0%, ${K.ink} 55%, ${K.teak} 100%)`,
          color: "#fff",
        }}
      >
        {/* Unsplash backdrop — fades in when the search returns */}
        {heroPhoto?.image_url && (
          <div className="absolute inset-0 opacity-40 mix-blend-luminosity" aria-hidden="true">
            <img
              src={heroPhoto.image_url}
              alt=""
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>
        )}
        <div className="max-w-6xl mx-auto px-4 lg:px-6 py-16 sm:py-24 relative">
          <div className="grid md:grid-cols-[1fr_auto] items-center gap-8">
            <div>
              <span
                className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest rounded-full px-3 py-1"
                style={{ background: K.goldSoft, color: K.gold }}
                data-testid="keralatravels-brand-chip"
              >
                <Waves className="w-3 h-3" /> A Social Pulse network site · Kerala Travels
              </span>
              <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight">
                Kerala Travels
              </h1>
              <p className="mt-3 text-lg max-w-[46ch] opacity-90">{HERO_LINES[0]}</p>
              <p className="text-sm opacity-80 mt-1">{HERO_LINES[1]}</p>

              <div className="mt-6 flex flex-wrap gap-2">
                <Link
                  to="/l/india/kerala"
                  className="rounded-full px-4 py-2 text-sm font-semibold shadow-sm hover:shadow-md transition inline-flex items-center gap-1.5"
                  style={{ background: K.gold, color: "#fff" }}
                  data-testid="keralatravels-cta-portal"
                >
                  <Hotel className="w-4 h-4" /> Explore Kerala on the map
                </Link>
                <Link
                  to="/directory/indian-districts"
                  className="rounded-full px-4 py-2 text-sm font-semibold border-2 bg-white/10 hover:bg-white/20 transition"
                  style={{ borderColor: "#fff" }}
                  data-testid="keralatravels-cta-districts"
                >
                  All 14 districts
                </Link>
                <Link
                  to="/stamps?country=India"
                  className="rounded-full px-4 py-2 text-sm font-semibold border-2 bg-white/10 hover:bg-white/20 transition"
                  style={{ borderColor: "#fff" }}
                  data-testid="keralatravels-cta-stamps"
                >
                  City crests
                </Link>
              </div>
            </div>

            {/* Kerala state stamp */}
            <PlaceStamp
              kind="state"
              slug="india/kerala"
              size={160}
              className="shadow-lg ring-4 ring-white/40 hidden md:block"
              title="Kerala"
              testid="keralatravels-hero-stamp"
              autoFetch
            />
          </div>
        </div>
      </section>

      {/* Unsplash attribution — sits under the hero, only when the API served a photo */}
      {heroPhoto?.photographer && (
        <div
          className="max-w-6xl mx-auto px-4 lg:px-6 pt-2 pb-1"
          data-testid="keralatravels-hero-credit-strip"
        >
          <UnsplashCredit
            photographer={heroPhoto.photographer}
            unsplashHref={heroPhoto.unsplash_link}
            align="right"
            className="text-xs"
            testid="keralatravels-hero-credit"
          />
        </div>
      )}

      {/* Featured tourism cities — each city fans out to its POI categories */}
      <section className="max-w-6xl mx-auto px-4 lg:px-6 py-12" data-testid="keralatravels-featured-cities">
        <div className="flex items-baseline gap-2 mb-6">
          <Hotel className="w-4 h-4" style={{ color: K.accent }} />
          <h2 className="font-display text-3xl" style={{ color: K.ink }}>Featured tourism cities</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FEATURED_CITIES.map((c) => (
            <FeaturedCity key={c.slug} city={c} palette={K} />
          ))}
        </div>
      </section>

      {/* 14 Kerala districts */}
      <section className="max-w-6xl mx-auto px-4 lg:px-6 py-8" data-testid="keralatravels-districts">
        <div className="flex items-baseline gap-2 mb-6">
          <MapPin className="w-4 h-4" style={{ color: K.accent }} />
          <h2 className="font-display text-3xl" style={{ color: K.ink }}>Every district</h2>
          <span className="text-xs font-mono opacity-60">
            {loading ? "…" : districts.length}
          </span>
        </div>
        {loading ? (
          <div className="opacity-60 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading districts…</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {districts.map((d) => (
              <Link
                key={d.slug}
                to={`/l/${d.slug}`}
                className="group flex items-center gap-3 rounded-xl border-2 p-3 hover:shadow-md hover:-translate-y-0.5 transition"
                style={{ borderColor: K.accentSoft, background: "#fff" }}
                data-testid={`keralatravels-district-${d.slug}`}
              >
                <PlaceStamp kind="district" slug={d.slug} override={d.stamp_url} size={44} className="shadow-sm ring-1 ring-white/60" title={d.name} />
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate group-hover:underline" style={{ color: K.ink }}>{d.name}</div>
                  <div className="mt-0.5">
                    <PlaceKindTag kind="district" slug={d.slug} country="india" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Fresh Kerala Travels pulses (only when publishers have shipped some) */}
      {pulses.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 lg:px-6 py-8 pb-12" data-testid="keralatravels-pulses">
          <div className="flex items-baseline gap-2 mb-4">
            <Sparkles className="w-4 h-4" style={{ color: K.gold }} />
            <h2 className="font-display text-3xl" style={{ color: K.ink }}>Fresh travel pulses</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pulses.map((p) => (
              <Link
                key={p.id}
                to={`/pulse/${p.slug || p.id}`}
                className="rounded-xl border p-4 hover:shadow-md hover:-translate-y-0.5 transition bg-white"
                style={{ borderColor: K.goldSoft }}
                data-testid={`keralatravels-pulse-${p.id}`}
              >
                <div className="text-xs font-mono uppercase tracking-widest opacity-60">{p.city || "Kerala"}</div>
                <div className="mt-1 font-display text-lg leading-tight">{p.title}</div>
                {p.description && (
                  <p className="text-sm opacity-75 mt-1 line-clamp-3">{p.description}</p>
                )}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Weather Almanac cross-post — auto-hides when quiet. */}
      <section className="max-w-6xl mx-auto px-4 lg:px-6">
        <LocationWeatherPanel slug="kerala" />
      </section>

      {/* Preview footer */}
      <footer className="max-w-6xl mx-auto px-4 lg:px-6 py-8 opacity-70 text-xs" data-testid="keralatravels-footer">
        Preview of the standalone <b>Kerala Travels</b> site — this page will
        move to <a className="underline" href="https://hotel-booking-beta.emergent.host">hotel-booking-beta.emergent.host</a> in
        production. Every listing, stamp, district and pulse pulls from the
        shared Social Pulse network.
      </footer>
    </div>
  );
}

/**
 * FeaturedCity — one card per Kerala tourism city, showing a rotating
 * peek of attractions / parks / museums / monuments. Uses the parent
 * `/api/geo/places/directory/<city>` endpoint so we automatically get
 * whatever the Geographic Discovery Engine has surfaced so far.
 */
function FeaturedCity({ city, palette }) {
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/geo/places/directory/${city.slug}`, { params: { limit: 6 } })
      .then((r) => {
        if (!alive) return;
        // Keep only travel-relevant categories, in the order we care about.
        const wanted = new Set(TRAVEL_CATEGORIES.map((c) => c.slug));
        const filtered = (r.data?.categories || []).filter((b) => wanted.has(b.category));
        setBuckets(filtered);
      })
      .catch(() => alive && setBuckets([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [city.slug]);

  const totalPois = buckets.reduce((n, b) => n + (b.items?.length || 0), 0);

  return (
    <article
      className="rounded-2xl border-2 p-4 bg-white"
      style={{ borderColor: palette.accentSoft }}
      data-testid={`keralatravels-city-${city.slug}`}
    >
      <div className="flex items-start gap-3">
        <PlaceStamp
          kind="city"
          slug={city.slug}
          size={56}
          className="shadow-sm ring-2 ring-white shrink-0"
          title={city.name}
          autoFetch
        />
        <div className="min-w-0 flex-1">
          <Link
            to={`/l/${city.slug}`}
            className="block font-display text-xl leading-tight hover:underline"
            style={{ color: palette.ink }}
            data-testid={`keralatravels-city-name-${city.slug}`}
          >
            {city.name}
          </Link>
          <div className="text-xs opacity-70 mt-0.5">{city.tagline}</div>
        </div>
        <Link
          to={`/places/${city.slug}`}
          className="shrink-0 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-0.5 hover:underline"
          style={{ color: palette.accent }}
          data-testid={`keralatravels-city-all-${city.slug}`}
          title="Browse every attraction, park, museum and monument"
        >
          All <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      {loading ? (
        <div className="mt-3 text-xs opacity-60"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Loading…</div>
      ) : totalPois === 0 ? (
        <div className="mt-3 text-xs opacity-60">
          POIs will populate here as the Geographic Discovery Engine indexes {city.name}.
        </div>
      ) : (
        <ul className="mt-3 space-y-1.5" data-testid={`keralatravels-city-list-${city.slug}`}>
          {buckets.slice(0, 3).map((b) => {
            const meta = TRAVEL_CATEGORIES.find((c) => c.slug === b.category);
            const Icon = meta?.Icon || MapPin;
            const items = (b.items || []).slice(0, 3);
            if (items.length === 0) return null;
            return (
              <li key={b.category}>
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-70 flex items-center gap-1">
                  <Icon className="w-3 h-3" /> {meta?.label || b.label}
                </div>
                <ul className="mt-0.5 ml-4 list-disc marker:opacity-40">
                  {items.map((it) => (
                    <li key={it.slug} className="text-sm">
                      <Link
                        to={`/places/detail/${it.slug}`}
                        className="hover:underline"
                        style={{ color: palette.inkSoft }}
                      >
                        {it.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
            );
          })}
        </ul>
      )}
    </article>
  );
}
